import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import Button from '@adsk/bim360-matrix-react-button';
import Field from '@adsk/bim360-matrix-react-field';
import TextInput, { Behaviors } from '@adsk/bim360-matrix-react-text-input';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import SaveButton from '@adsk/bim360-matrix-react-save-button';
import {
  NotificationTypes,
  NotificationLevels
} from '@adsk/bim360-matrix-react-notification-manager';
import { useNotification } from '@adsk/bim360-matrix-react-notification-manager/es/helpers/context';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';

import CreateEntityForm from '../../CreateEntityForm';
import formatPcoName from './helpers/formatPCOName';

import './_PcoCreateForm.scss';

const PCOCreateForm = ({
  onClose,
  onCreate,
  onCreateSuccess,
  initialFields,
  sourceEntityType,
  sourceEntityNumber
}) => {
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));

  const { name: initialName, description: initialDescription } = initialFields || {};

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const { showNotification } = useNotification();

  const handleNameChange = value => setName(value);
  const handleDescriptionChange = value => setDescription(value);

  const handleClose = () => {
    setName(initialName);
    setDescription(initialDescription);
    onClose();
  };

  const handleView = deeplinkUrl => () => {
    window.open(deeplinkUrl);
  };

  const handleCreate = () => {
    return onCreate({
      name,
      description,
      sourceType: sourceEntityType,
      sourceRef: sourceEntityNumber
    })
      .then(entity => {
        handleClose();
        onCreateSuccess(entity);
        showNotification({
          type: NotificationTypes.TOAST,
          level: NotificationLevels.SUCCESS,
          message: T.translate('CREATE_SUCCESS', {
            name: (
              <AnchorButton onClick={handleView(entity.deeplinkUrl)}>
                {formatPcoName(entity)}
              </AnchorButton>
            )
          })
        });
      })
      .catch(() => {
        showNotification({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: T.translate('CREATE_FAILED')
        });
      });
  };

  return (
    <CreateEntityForm>
      <CreateEntityForm.Content>
        <div className="PcoCreateForm">
          <Field
            className="PcoCreateForm__field PcoCreateForm__field--required"
            label={T.translate('NAME')}
          >
            <TextInput value={name} onChange={handleNameChange} />
          </Field>
          <Field className="PcoCreateForm__field" label={T.translate('DESCRIPTION')}>
            <TextInput
              value={description}
              onChange={handleDescriptionChange}
              inputProps={{
                behavior: Behaviors.MULTI_LINE,
                className: 'PcoCreateForm__field-input--multi'
              }}
            />
          </Field>
        </div>
      </CreateEntityForm.Content>
      <CreateEntityForm.Footer>
        <Button styleType={Button.StyleType.LINK_SECONDARY} onClick={handleClose}>
          {T.translate('CANCEL')}
        </Button>
        <SaveButton
          disabled={!(name && name.trim())}
          onSave={handleCreate}
          saveText={T.translate('CREATE')}
          savingText={T.translate('CREATING')}
        />
      </CreateEntityForm.Footer>
    </CreateEntityForm>
  );
};

PCOCreateForm.propTypes = {
  /**
   * Callback that's being called when close the modal.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Callback that's being called when click the create button.
   */
  onCreate: PropTypes.func.isRequired,
  /**
   * Callback that's being called when create PCO successfully.
   */
  onCreateSuccess: PropTypes.func.isRequired,
  /**
   * The initial fields of PCO creation form.
   */
  initialFields: PropTypes.shape({
    /**
     * The initial name of PCO
     */
    name: PropTypes.string,
    /**
     * The initial description of pco
     */
    description: PropTypes.string
  }),
  /**
   * The source entity type to create the PCO
   */
  sourceEntityType: PropTypes.oneOf(objectValues(ENTITY_TYPES)),
  /**
   * The user friendly number that try to linked the created PCO
   */
  sourceEntityNumber: PropTypes.string
};

export default PCOCreateForm;
